// Imports
import { faHome } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import theme from '../../../../../../shareables/theme';
import { GetAccountsResponse } from '../../../../../../shareables/types/api/main/website/page/type/207/accounts/get';
import UserAvatar from '../../../../shared-components/user-avatar';


// Styled components
const Container = styled(Link)`
	break-inside: avoid;
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: auto 1fr;
	
	background: #fafafa;
	border: 0.0625rem solid #eee;
	border-radius: 0.1875rem;
	padding: 1rem;
	
	color: inherit !important;
	text-decoration: none !important;
	
	&:not(:last-child) {
		margin-bottom: 1rem;
	}
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		grid-template-rows: auto auto auto;
		grid-template-columns: 1fr;
	}
`;

const Header = styled.h3`
	display: inline-block;
	margin-bottom: 0.5rem;
	line-height: 1;
	font-weight: 400;
	font-size: 1.15rem;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		text-align: center;
		margin-bottom: 1rem;
	}
`;

const Addresses = styled.div`
	grid-area: 2 / 1 / 3 / 2;
	
	@media (max-width: ${theme.breakpoints.mobile}px) {
		grid-area: 3 / 1 / 4 / 2;
	}
`;

const Members = styled.div<{ displayInViewportSize: 'small' | 'large' }>`
	width: 100%;
	display: none;
	flex-wrap: wrap;
	align-items: center;
	align-content: center;
	justify-content: flex-end;
	padding-left: 0.75rem;
	
	${({ displayInViewportSize }) =>
		displayInViewportSize === 'small'
			? css`
					@media (max-width: ${theme.breakpoints.mobile}px) {
						display: flex;
						justify-content: center;
						grid-area: 1 / 1 / 1 / 1;
						margin-bottom: 0.5rem;
					}
				`
			: css`
					@media (min-width: ${theme.breakpoints.mobile + 1}px) {
						display: flex;
						grid-area: 1 / 2 / 3 / 3;
					}
				`}
`;

const Address = styled.div`
	display: flex;
	align-items: center;
	line-height: 1.2;
	
	&:not(:first-child) {
		margin-top: 0.5rem;
		padding-top: 0.5rem;
		border-top: 0.0625rem solid #eee;
	}
	
	svg {
		margin-right: 0.5rem;
	}
`;

const ProfileAvatar = styled(UserAvatar)`
	display: inline-block;
	padding: 0.8125rem 0;
	text-align: center;
	font-size: 1.5rem;
`;

const AvatarContainer = styled.div`
	margin-top: -0.0625rem;
	margin-bottom: -0.0625rem;
`;

const Member = styled.div`
	line-height: 1;
	margin-left: -0.75rem;
	
	img,
	${ProfileAvatar} {
		width: 3.25rem;
		height: 3.25rem;
		border-radius: 50%;
		border: 0.0625rem solid #fafafa;
	}
	
	span:not(${ProfileAvatar}) {
		display: flex;
		align-items: baseline;
		
		a {
			color: inherit;
		}
		
		small {
			margin-left: 0.4rem;
			font-size: 0.75rem;
			font-style: italic;
			color: #999;
		}
	}
`;


// Define the accepted props
interface Props {
	account: GetAccountsResponse[0];
}


// Function component
const Account: React.FC<Props> = ({ account }) => {
	// Build members JSX
	const membersJSX = account.members.map((member, i) => {
		return (
			<Member key={i}>
				<AvatarContainer>
					{member.profilePhotoURL ? (
						<img src={member.profilePhotoURL} alt='' />
					) : (
						<ProfileAvatar firstName={member.firstName} lastName={member.lastName} round />
					)}
				</AvatarContainer>
			</Member>
		);
	});
	
	return (
		<Container to={`account/${account.id}`}>
			<Members displayInViewportSize='small'>{membersJSX}</Members>
			
			<Header>{account.consolidatedName}</Header>
			
			<Members displayInViewportSize='large'>{membersJSX}</Members>
			
			<Addresses>
				{account.addresses.length === 0 && (
					<span className='is-gray'>
						<em>No addresses in this community…</em>
					</span>
				)}
				
				{account.addresses.map((address, i) => (
					<Address key={i}>
						<FontAwesomeIcon icon={faHome} />
						{address.formatted}
					</Address>
				))}
			</Addresses>
		</Container>
	);
};

export default Account;
